import { Path } from "../../../../../static/Constants";
import { colors } from "../../../../../theme";
import TextField from "../../../../Components/ui/inputs/CommonTextField";
import Button from "../../../../Components/ui/inputs/CommonButton";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { RefreshCw } from "lucide-react";
import Api, { apiError } from "../../../../Service/Api";
import { useAccount } from "../../../../Context/AccountContext";
import { Link, useHistory, useLocation } from "react-router-dom";
import { displayBalance } from "../../../../../static/balance";

export default function WalletContent({ section }) {
  const { user, wallet, refreshWallet, walletError } = useAccount();
  const history = useHistory();
  const location = useLocation();
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [transactions, setTransactions] = useState({
    items: [],
    total: 0,
    page: 0,
  });
  const [deposits, setDeposits] = useState([]);
  const [queue, setQueue] = useState([]);
  const requestedPage = Number(
    new URLSearchParams(location.search).get("page") || 1
  );
  const page =
    Number.isSafeInteger(requestedPage) &&
    requestedPage > 0 &&
    requestedPage <= 10000
      ? requestedPage
      : 1;
  const setPage = (value) =>
    history.push(`${Path.walletTransactions}?page=${value}`);
  const historyReady = transactions.page === page;
  const [review, setReview] = useState(null);
  const [reference, setReference] = useState("");
  const requestId = useRef(null);
  const inFlight = useRef(false);
  const loadSequence = useRef(0);

  const load = useCallback(async () => {
    if (!user) return;
    const request = ++loadSequence.current;
    setBusy(true);
    setError("");
    try {
      const [, result] = await Promise.all([
        refreshWallet(),
        section === "transactions"
          ? Api.get(`/wallet/transactions?page=${page}`)
          : section === "add-balance"
          ? Api.get("/wallet/deposits")
          : section === "approvals"
          ? Api.get("/wallet/admin/deposits")
          : Promise.resolve(null),
      ]);
      if (request !== loadSequence.current) return;
      if (section === "transactions") setTransactions({ ...result.data, page });
      if (section === "add-balance") setDeposits(result.data);
      if (section === "approvals") setQueue(result.data);
    } catch (failure) {
      if (request === loadSequence.current) setError(apiError(failure));
    } finally {
      if (request === loadSequence.current && !inFlight.current) setBusy(false);
    }
  }, [user, page, section, refreshWallet]);

  useEffect(() => {
    load();
    return () => {
      loadSequence.current += 1;
    };
  }, [load]);
  useEffect(() => {
    requestId.current = null;
  }, [amount]);

  const requestDeposit = async (event) => {
    event.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    loadSequence.current += 1;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      if (!requestId.current) requestId.current = window.crypto.randomUUID();
      const { data } = await Api.post("/wallet/deposits", {
        amount,
        requestId: requestId.current,
      });
      setMessage(
        data.message ||
          "Deposit request recorded. Awaiting payment confirmation."
      );
      setAmount("");
      requestId.current = null;
      await load();
    } catch (failure) {
      setError(apiError(failure));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const reviewDeposit = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    loadSequence.current += 1;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await Api.post(`/wallet/admin/deposits/${review.id}/review`, {
        action: review.action,
        reference: reference.trim(),
      });
      setMessage(
        review.action === "approve"
          ? "Payment confirmed and wallet credited."
          : "Deposit rejected. No balance change."
      );
      setReview(null);
      setReference("");
      await load();
    } catch (failure) {
      setError(apiError(failure));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          p: 3,
          borderRadius: 3,
          color: colors.neutral.white,
          background: `linear-gradient(120deg,${colors.neutral.textTitle},${colors.primary.dark})`,
          "& .MuiTypography-root": {
            color: `${colors.neutral.white} !important`,
          },
        }}
      >
        <Typography sx={{ opacity: 0.75 }}>Available balance</Typography>
        <Typography
          component="p"
          sx={{ fontSize: 36, fontWeight: 750, overflowWrap: "anywhere" }}
        >
          {walletError ? "Unavailable" : displayBalance(wallet?.balance)}{" "}
          <Box
            component="span"
            sx={{ fontSize: 14, color: `${colors.neutral.white} !important` }}
          >
            credits
          </Box>
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          Planned withdrawal redemption: 1 credit = 1 BET.
        </Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {message && (
        <Alert severity="success" role="status">
          {message}
        </Alert>
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography component="h2" variant="h6" fontWeight={700}>
          {
            {
              overview: "Wallet overview",
              "add-balance": "Add balance",
              transactions: "Transaction history",
              approvals: "Deposit approvals",
            }[section]
          }
        </Typography>
        <IconButton aria-label="Refresh wallet" onClick={load} disabled={busy}>
          <RefreshCw size={18} />
        </IconButton>
      </Stack>
      {busy && <CircularProgress size={22} aria-label="Loading wallet" />}
      {section === "overview" && (
        <Stack spacing={2}>
          <Typography>
            Your balance is held in your AutoBET account. Confirmed credits are
            recorded in your transaction history.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              component={Link}
              to={Path.walletAddBalance}
              variant="contained"
            >
              Add balance
            </Button>
            <Button
              component={Link}
              to={Path.walletTransactions}
              variant="outlined"
            >
              View transactions
            </Button>
          </Stack>
          <Alert severity="info">
            Withdrawals and wallet-funded games are coming in a later update.
          </Alert>
        </Stack>
      )}
      {section === "add-balance" && (
        <Stack spacing={2}>
          <Alert severity="info">
            {wallet?.depositInstructions || "Payment methods are coming soon."}
          </Alert>
          {wallet?.manualDepositsEnabled && (
            <Box component="form" onSubmit={requestDeposit}>
              <Stack spacing={2}>
                <TextField
                  label="Amount in credits"
                  required
                  value={amount}
                  disabled={busy}
                  onChange={(event) => setAmount(event.target.value)}
                  inputProps={{
                    inputMode: "decimal",
                    pattern: "(?:0|[1-9][0-9]{0,8})(?:\\.[0-9]{1,2})?",
                    maxLength: 12,
                  }}
                  helperText="Your balance changes after payment is confirmed."
                />
                <Button type="submit" variant="contained" disabled={busy}>
                  Request deposit
                </Button>
              </Stack>
            </Box>
          )}
          <Typography component="h2" variant="subtitle1" fontWeight={700}>
            Recent deposit requests
          </Typography>
          {!deposits.length && !busy && !error && (
            <Typography color="text.secondary">
              No deposit requests yet.
            </Typography>
          )}
          {deposits.map((deposit) => (
            <Stack
              key={deposit.id}
              direction="row"
              justifyContent="space-between"
              sx={{
                p: 1.5,
                border: `1px solid ${colors.neutral.border}`,
                borderRadius: 2,
              }}
            >
              <Box>
                <Typography>
                  {displayBalance(deposit.amount)} credits
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(deposit.createdAt).toLocaleString()}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={deposit.status}
                color={
                  deposit.status === "approved"
                    ? "success"
                    : deposit.status === "rejected"
                    ? "error"
                    : "default"
                }
              />
            </Stack>
          ))}
        </Stack>
      )}
      {section === "transactions" && (
        <Stack spacing={1.5}>
          {historyReady && !transactions.items.length && !busy && !error && (
            <Typography color="text.secondary">
              No transactions yet. Confirmed balance changes will appear here.
            </Typography>
          )}
          {(historyReady ? transactions.items : []).map((item) => (
            <Box
              key={item.id}
              sx={{
                p: 2,
                border: `1px solid ${colors.neutral.border}`,
                borderRadius: 2,
              }}
            >
              <Stack direction="row" justifyContent="space-between">
                <Typography fontWeight={650}>{item.type}</Typography>
                <Typography color="success.main">
                  +{displayBalance(item.amount)} credits
                </Typography>
              </Stack>
              <Typography variant="body2">
                Balance after: {displayBalance(item.balanceAfter)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", overflowWrap: "anywhere" }}
              >
                {new Date(item.createdAt).toLocaleString()} · {item.reference}
              </Typography>
            </Box>
          ))}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              variant="text"
              disabled={busy || page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Typography variant="body2">Page {page}</Typography>
            <Button
              variant="text"
              disabled={
                busy ||
                !historyReady ||
                page >= 10000 ||
                page * 20 >= transactions.total
              }
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Stack>
        </Stack>
      )}
      {section === "approvals" && user?.role === "admin" && (
        <Stack spacing={2}>
          <Alert severity="warning">
            Confirm the payment independently before approving. Each payment
            reference can be used once.
          </Alert>
          {!queue.length && !busy && !error && (
            <Typography>No pending deposits.</Typography>
          )}
          {queue.map((deposit) => (
            <Box
              key={deposit.id}
              sx={{
                p: 2,
                border: `1px solid ${colors.neutral.border}`,
                borderRadius: 2,
              }}
            >
              <Typography sx={{ overflowWrap: "anywhere" }}>
                {deposit.email}
              </Typography>
              <Typography fontWeight={700}>
                {displayBalance(deposit.amount)} credits
              </Typography>
              <Stack direction="row">
                <Button
                  variant="text"
                  disabled={busy || deposit.userId === user.id}
                  onClick={() => {
                    setReview({ ...deposit, action: "approve" });
                    setReference("");
                    setMessage("");
                    setError("");
                  }}
                >
                  Review payment
                </Button>
                <Button
                  variant="dangerText"
                  disabled={busy || deposit.userId === user.id}
                  onClick={() => {
                    setReview({ ...deposit, action: "reject" });
                    setReference("");
                    setMessage("");
                    setError("");
                  }}
                >
                  Reject
                </Button>
              </Stack>
            </Box>
          ))}
          {review && (
            <Box
              sx={{ p: 2, background: colors.neutral.divider, borderRadius: 2 }}
            >
              <Stack spacing={2}>
                <Typography>
                  {review.action === "approve"
                    ? "Confirm payment for"
                    : "Reject request for"}{" "}
                  {review.email}: {displayBalance(review.amount)} credits
                </Typography>
                <TextField
                  label={
                    review.action === "approve"
                      ? "Unique payment reference"
                      : "Rejection reason"
                  }
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                  disabled={busy}
                  inputProps={{ minLength: 5, maxLength: 120 }}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    disabled={busy || reference.trim().length < 5}
                    onClick={reviewDeposit}
                  >
                    {review.action === "approve"
                      ? "Confirm and credit"
                      : "Confirm rejection"}
                  </Button>
                  <Button
                    variant="text"
                    disabled={busy}
                    onClick={() => setReview(null)}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  );
}
